import React, { useState, useEffect } from "react";
import Service from "../../Service/firebase";
import "./servicegroupslist.css";

const ServiceGroupsList = () => {
  const [groupsData, setGroupsData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newGroupInputs, setNewGroupInputs] = useState({});
  const [savingFor, setSavingFor] = useState(null);
  const [newEntryCategory, setNewEntryCategory] = useState("");
  const [newEntryGroups, setNewEntryGroups] = useState([]);
  const [newEntryInput, setNewEntryInput] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    onFetchData();
  }, []);

  const onFetchData = async () => {
    setIsLoading(true);
    const [fetchedGroups, fetchedCategories] = await Promise.all([
      Service.getAllServiceGroups(),
      Service.getAllData("category"),
    ]);

    // Build a map of categoryId -> categoryName for display
    const categoryMap = {};
    fetchedCategories.forEach((c) => {
      categoryMap[c.categoryId] = c.name;
    });
    setCategories(categoryMap);
    setAllCategories(fetchedCategories);
    setGroupsData(fetchedGroups);
    setIsLoading(false);
  };

  const handleAddGroup = async (categoryId, currentGroups) => {
    const newGroup = (newGroupInputs[categoryId] || "").trim();
    if (!newGroup) return;
    if (currentGroups.includes(newGroup)) return;

    setSavingFor(categoryId);
    const updatedGroups = [...currentGroups, newGroup];
    await Service.updateServiceGroups(categoryId, updatedGroups);

    setGroupsData((prev) =>
      prev.map((entry) =>
        entry.categoryId === categoryId
          ? { ...entry, groups: updatedGroups }
          : entry
      )
    );
    setNewGroupInputs((prev) => ({ ...prev, [categoryId]: "" }));
    setSavingFor(null);
  };

  // Categories that don't have a servicesGroups entry yet
  const existingCategoryIds = new Set(groupsData.map((e) => e.categoryId));
  const availableCategories = allCategories.filter(
    (c) => !existingCategoryIds.has(c.categoryId)
  );

  const handleNewEntryAddTag = () => {
    const tag = newEntryInput.trim();
    if (!tag || newEntryGroups.includes(tag)) return;
    setNewEntryGroups((prev) => [...prev, tag]);
    setNewEntryInput("");
  };

  const handleNewEntryRemoveTag = (tag) => {
    setNewEntryGroups((prev) => prev.filter((g) => g !== tag));
  };

  const handleCreateServiceGroup = async () => {
    if (!newEntryCategory || newEntryGroups.length === 0) return;
    setIsCreating(true);
    await Service.updateServiceGroups(newEntryCategory, newEntryGroups);
    setGroupsData((prev) => [
      ...prev,
      { categoryId: newEntryCategory, groups: newEntryGroups },
    ]);
    setNewEntryCategory("");
    setNewEntryGroups([]);
    setNewEntryInput("");
    setIsCreating(false);
  };

  const handleRemoveGroup = async (categoryId, groupName, currentGroups) => {
    const updatedGroups = currentGroups.filter((g) => g !== groupName);
    setSavingFor(categoryId);
    await Service.updateServiceGroups(categoryId, updatedGroups);

    setGroupsData((prev) =>
      prev.map((entry) =>
        entry.categoryId === categoryId
          ? { ...entry, groups: updatedGroups }
          : entry
      )
    );
    setSavingFor(null);
  };

  return (
    <div className="serviceGroupsPage">
      <div className="container main_section mainSection">
        <div className="topHeader">
          <ul>
            <li>
              <i
                className="fa fa-arrow-circle-o-right fa-2x"
                aria-hidden="true"
              ></i>
            </li>
            <li>
              <h5>Service Groups</h5>
            </li>
          </ul>
        </div>
        <hr />

        {isLoading ? (
          <div className="text-center py-4">
            <i className="fa fa-spinner fa-spin fa-2x"></i>
          </div>
        ) : groupsData.length === 0 ? (
          <p className="text-muted">No service groups found.</p>
        ) : (
          groupsData.map((entry) => (
            <div key={entry.categoryId} className="categoryCard">
              <div className="categoryTitle">
                {categories[entry.categoryId] || entry.categoryId}
                <span
                  style={{ color: "#888", fontSize: "12px", marginLeft: "8px" }}
                >
                  ({entry.categoryId})
                </span>
              </div>

              <div>
                {entry.groups.map((group) => (
                  <span key={group} className="groupTag">
                    {group}
                    <button
                      className="removeBtn"
                      title="Remove group"
                      disabled={savingFor === entry.categoryId}
                      onClick={() =>
                        handleRemoveGroup(
                          entry.categoryId,
                          group,
                          entry.groups
                        )
                      }
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </span>
                ))}
                {entry.groups.length === 0 && (
                  <span style={{ color: "#aaa", fontSize: "13px" }}>
                    No groups yet
                  </span>
                )}
              </div>

              <div className="addGroupRow">
                <input
                  type="text"
                  placeholder="New group name"
                  value={newGroupInputs[entry.categoryId] || ""}
                  onChange={(e) =>
                    setNewGroupInputs((prev) => ({
                      ...prev,
                      [entry.categoryId]: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleAddGroup(entry.categoryId, entry.groups);
                    }
                  }}
                />
                <button
                  className="btn btn-primary btn-sm"
                  disabled={
                    savingFor === entry.categoryId ||
                    !(newGroupInputs[entry.categoryId] || "").trim()
                  }
                  onClick={() => handleAddGroup(entry.categoryId, entry.groups)}
                >
                  {savingFor === entry.categoryId ? (
                    <i className="fa fa-spinner fa-spin"></i>
                  ) : (
                    "Add"
                  )}
                </button>
              </div>
            </div>
          ))
        )}

        {/* Add new service group for a category */}
        {!isLoading && availableCategories.length > 0 && (
          <div className="categoryCard newEntryCard">
            <div className="categoryTitle">Add Service Group for New Category</div>

            <div className="form-row align-items-end" style={{ gap: "12px", display: "flex", flexWrap: "wrap" }}>
              <div>
                <label style={{ fontSize: "13px", marginBottom: "4px", display: "block" }}>
                  Category
                </label>
                <select
                  className="form-control form-control-sm"
                  style={{ minWidth: "200px" }}
                  value={newEntryCategory}
                  onChange={(e) => setNewEntryCategory(e.target.value)}
                >
                  <option value="">Select Category</option>
                  {availableCategories.map((c) => (
                    <option key={c.categoryId} value={c.categoryId}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: "13px", marginBottom: "4px", display: "block" }}>
                  Add Groups
                </label>
                <div className="addGroupRow" style={{ marginTop: 0 }}>
                  <input
                    type="text"
                    placeholder="Group name"
                    value={newEntryInput}
                    onChange={(e) => setNewEntryInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleNewEntryAddTag();
                    }}
                  />
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={!newEntryInput.trim()}
                    onClick={handleNewEntryAddTag}
                  >
                    + Tag
                  </button>
                </div>
              </div>
            </div>

            {newEntryGroups.length > 0 && (
              <div style={{ marginTop: "10px" }}>
                {newEntryGroups.map((tag) => (
                  <span key={tag} className="groupTag">
                    {tag}
                    <button
                      className="removeBtn"
                      onClick={() => handleNewEntryRemoveTag(tag)}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div style={{ marginTop: "14px" }}>
              <button
                className="btn btn-primary btn-sm"
                style={{ borderRadius: "20px" }}
                disabled={
                  isCreating ||
                  !newEntryCategory ||
                  newEntryGroups.length === 0
                }
                onClick={handleCreateServiceGroup}
              >
                {isCreating ? (
                  <i className="fa fa-spinner fa-spin"></i>
                ) : (
                  "Create Service Group"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceGroupsList;
